/**
 * Audit-context headers consumed by hiya-backend's `hiya_audit`
 * middleware (PDPL/GDPR audit logging) on every outbound request:
 *
 * - `X-Session-Id` — per-tab UUID, in-memory only (never persisted)
 * - `X-Platform` — `"web"` constant
 * - `X-App-Version` — application version (caller-provided, normally
 *   `import.meta.env.VITE_APP_VERSION`)
 * - `X-Application-Language` — current locale code (caller-provided
 *   via the `getLanguage` callback; falls back to `navigator.language`)
 *
 * `X-Device-Id` is intentionally omitted: GDPR Recital 30 +
 * Art. 5(1)(c) data minimisation. Forensic correlation is already
 * covered server-side by `session_id` + `ip_address_hash` (/24 + HMAC
 * pepper) + `user_agent_hash`. See hiya-backend
 * `packages/hiya-audit/hiya_audit/schema.py` module docstring.
 */

const PLATFORM_WEB = "web";

let sessionId: string | undefined;

function generateUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const buf = new Uint8Array(16);
    crypto.getRandomValues(buf);
    buf[6] = (buf[6]! & 0x0f) | 0x40;
    buf[8] = (buf[8]! & 0x3f) | 0x80;
    const hex = Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  // Web Crypto must be available — both portals run in browsers with
  // `secure-context` already (HTTPS-only deployment). Falling back to
  // `Math.random()` would be both insecure (SonarCloud Hotspot S2245)
  // and pointless: an environment without Web Crypto cannot meaningfully
  // run the rest of the auth/storage stack.
  throw new Error(
    "crypto.getRandomValues unavailable; cannot generate audit session id",
  );
}

function getOrCreateSessionId(): string {
  sessionId ??= generateUuid();
  return sessionId;
}

/**
 * Test-only: reset the in-memory session id. Each browser-tab session
 * normally keeps the same id from open to close; tests drive the same
 * lifecycle by clearing it between cases.
 */
export function _resetSessionIdForTests(): void {
  sessionId = undefined;
}

export interface AuditContextOptions {
  /** App version from `import.meta.env.VITE_APP_VERSION` (or similar). */
  appVersion?: string;
  /** Returns the current locale code (`"en"` / `"ar"` / etc.). */
  getLanguage?: () => string | undefined;
}

/**
 * Build a fresh audit-context header map. Each call generates the same
 * session id (per-tab) but re-evaluates the language/version inputs so
 * a locale change between requests is reflected on the next request.
 */
export function buildAuditContextHeaders(
  options: AuditContextOptions = {},
): Record<string, string> {
  const headers: Record<string, string> = {
    "X-Session-Id": getOrCreateSessionId(),
    "X-Platform": PLATFORM_WEB,
  };

  const version = options.appVersion;
  if (version) {
    headers["X-App-Version"] = version;
  }

  // `globalThis.navigator?.` avoids the SSR `ReferenceError` on bare
  // `navigator` in non-browser builds without the negated-typeof guard
  // SonarCloud S7735 flags.
  const language = options.getLanguage?.() ?? globalThis.navigator?.language;
  if (language) {
    // Normalise to the language tag's primary subtag (`"en-US"` -> `"en"`)
    // so the backend `_normalize_platform`-equivalent semantics match
    // the mobile app, which sends `"en"` / `"ar"`.
    headers["X-Application-Language"] = language.split("-")[0]!;
  }

  return headers;
}

/**
 * Adapter that turns [buildAuditContextHeaders] into the async
 * `() => Promise<HeadersInit>` shape consumed by `createApiFetch`.
 */
export function createAuditContextHeadersFn(
  options: AuditContextOptions = {},
): () => Promise<Record<string, string>> {
  return async () => buildAuditContextHeaders(options);
}
